/**
 * 验证码相关操作 Composable
 * @description 从 userStore 抽取的图形验证码和手机验证码逻辑
 */

import type { Ref } from 'vue'
import type {
  ApiResponse,
  ImageCaptchaResponse,
} from '@/types/auth'

import { authApi } from '@/api/auth'

import { handleLoginError } from './authActions'

/**
 * 验证码 Composable 的依赖接口
 */
export interface CaptchaComposableDeps {
  imageCaptcha: Ref<ImageCaptchaResponse | null>
  phoneCodeSending: Ref<boolean>
  phoneCodeCooldown: Ref<number>
  setImageCaptcha: (captcha: ImageCaptchaResponse | null) => void
}

/**
 * 创建验证码相关 actions
 */
export function useCaptchaActions(deps: CaptchaComposableDeps) {
  const {
    phoneCodeSending,
    phoneCodeCooldown,
    setImageCaptcha,
  } = deps

  // 获取图形验证码
  async function getImageCaptcha(): Promise<ApiResponse<ImageCaptchaResponse>> {
    try {
      const response = await authApi.getImageCaptcha()
      if (response.code === '00000' && response.data) {
        setImageCaptcha(response.data)
      }
      return response
    }
    catch (error: unknown) {
      console.error('[UserStore] 获取图形验证码失败:', error)
      return { code: 'B1000', msg: '获取验证码失败，请稍后重试', data: null }
    }
  }

  interface SendVerificationData {
    phone: string
    captchaId: string
    captchaCode: string
  }

  // 发送手机验证码
  async function sendVerificationCode(data: SendVerificationData): Promise<ApiResponse<boolean>> {
    try {
      phoneCodeSending.value = true

      const response = await authApi.sendVerificationCode(data)

      if (response.code === '00000') {
        // 开始倒计时
        phoneCodeCooldown.value = response.data?.cooldown || 60
        const timer = setInterval(() => {
          phoneCodeCooldown.value--
          if (phoneCodeCooldown.value <= 0) {
            clearInterval(timer)
          }
        }, 1000)

        return {
          code: response.code,
          msg: response.msg,
          data: response.code === '00000',
        }
      }
      else {
        // 业务错误
        const errorMsg = response.msg || '发送验证码失败'
        return { code: response.code, msg: errorMsg, data: false }
      }
    }
    catch (error: unknown) {
      const errorMsg = handleLoginError(error)
      return { code: 'B1000', msg: errorMsg, data: false }
    }
    finally {
      phoneCodeSending.value = false
    }
  }

  // 发送密码重置验证码
  async function sendPasswordResetCode(contact: string, type: 'email' | 'phone'): Promise<ApiResponse<boolean>> {
    try {
      phoneCodeSending.value = true

      let response
      if (type === 'email') {
        response = await authApi.sendPasswordResetEmail(contact)
      }
      else {
        response = await authApi.sendPasswordResetSms(contact)
      }

      if (response.code === '00000') {
        // 开始倒计时
        phoneCodeCooldown.value = 60
        const timer = setInterval(() => {
          phoneCodeCooldown.value--
          if (phoneCodeCooldown.value <= 0) {
            clearInterval(timer)
          }
        }, 1000)

        return { code: response.code, msg: response.msg, data: true }
      }
      else {
        return { code: response.code, msg: response.msg || '发送验证码失败', data: false }
      }
    }
    catch (error: unknown) {
      const errorMsg = handleLoginError(error)
      return { code: 'B1000', msg: errorMsg, data: false }
    }
    finally {
      phoneCodeSending.value = false
    }
  }

  // 验证密码重置验证码并获取重置 token
  async function verifyPasswordResetCode(data: {
    contact: string
    code: string
    contactType: 'email' | 'phone'
  }): Promise<ApiResponse<{ token: string } | null>> {
    try {
      const response = await authApi.verifyPasswordResetCode(data)
      return response
    }
    catch (error: unknown) {
      const errorMsg = handleLoginError(error)
      return { code: 'B1000', msg: errorMsg, data: null }
    }
  }

  // 重置密码
  async function resetPassword(data: {
    token: string
    newPassword: string
    confirmPassword?: string
  }): Promise<ApiResponse<void>> {
    try {
      const response = await authApi.resetPassword({
        token: data.token,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword ?? data.newPassword,
      })
      return response
    }
    catch (error: unknown) {
      const errorMsg = handleLoginError(error)
      return { code: 'B1000', msg: errorMsg, data: undefined as unknown as void }
    }
  }

  return {
    getImageCaptcha,
    sendVerificationCode,
    sendPasswordResetCode,
    verifyPasswordResetCode,
    resetPassword,
  }
}
